import React, { forwardRef, useState, useEffect, useRef } from 'react';
import { Typography } from '../Typography';
import { MdKeyboardArrowDown } from 'react-icons/md';
import styles from './PhoneNumberInput.module.scss';

export interface CountryCode {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
}

export interface PhoneNumberInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'defaultValue'> {
  label?: string;
  error?: string;
  helperText?: string;
  variant?: 'default' | 'outlined' | 'filled';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  required?: boolean;
  className?: string;
  defaultValue?: string;
  onCountryChange?: (country: CountryCode) => void;
  onPhoneChange?: (phone: string) => void;
}

const countries: CountryCode[] = [
  { code: 'AE', name: 'UAE', dialCode: '+971', flag: '🇦🇪' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
  { code: 'BR', name: 'Brazil', dialCode: '+55', flag: '🇧🇷' },
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' }, // نگه داشتن آمریکا به جای کانادا
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪' },
  { code: 'EG', name: 'Egypt', dialCode: '+20', flag: '🇪🇬' },
  { code: 'ES', name: 'Spain', dialCode: '+34', flag: '🇪🇸' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { code: 'IR', name: 'Iran', dialCode: '+98', flag: '🇮🇷' },
  { code: 'IT', name: 'Italy', dialCode: '+39', flag: '🇮🇹' },
  { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵' },
  { code: 'KR', name: 'South Korea', dialCode: '+82', flag: '🇰🇷' },
  { code: 'MX', name: 'Mexico', dialCode: '+52', flag: '🇲🇽' },
  { code: 'RU', name: 'Russia', dialCode: '+7', flag: '🇷🇺' },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', flag: '🇸🇦' },
  { code: 'TR', name: 'Turkey', dialCode: '+90', flag: '🇹🇷' },
];

const PhoneNumberInput = forwardRef<HTMLInputElement, PhoneNumberInputProps>(
  (
    {
      label,
      error,
      helperText,
      variant = 'default',
      size = 'md',
      fullWidth = false,
      required = false,
      className = '',
      onCountryChange,
      onPhoneChange,
      onChange,
      defaultValue,
      value,
      ...props
    },
    ref
  ) => {
    const [selectedCountry, setSelectedCountry] = useState<CountryCode>(countries[0]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [phoneValue, setPhoneValue] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Parse defaultValue to extract country and phone number
    useEffect(() => {
      if (defaultValue) {
        // Find matching country by dial code (try longest match first)
        const sortedCountries = countries.sort((a, b) => b.dialCode.length - a.dialCode.length);
        const matchingCountry = sortedCountries.find((country) =>
          defaultValue.startsWith(country.dialCode)
        );

        if (matchingCountry) {
          setSelectedCountry(matchingCountry);
          const phoneNumber = defaultValue.substring(matchingCountry.dialCode.length);
          setPhoneValue(phoneNumber);
        } else {
          // If no country code found, treat as phone number only
          setPhoneValue(defaultValue);
        }
      }
    }, [defaultValue]);

    // Parse value prop to extract country and phone number (for Formik)
    useEffect(() => {
      if (value && typeof value === 'string') {
        // Find matching country by dial code (try longest match first)
        const sortedCountries = countries.sort((a, b) => b.dialCode.length - a.dialCode.length);
        const matchingCountry = sortedCountries.find((country) =>
          value.startsWith(country.dialCode)
        );

        if (matchingCountry) {
          setSelectedCountry(matchingCountry);
          const phoneNumber = value.substring(matchingCountry.dialCode.length);
          setPhoneValue(phoneNumber);
        } else {
          // If no country code found, treat as phone number only
          setPhoneValue(value);
        }
      } else {
        // Reset phone value when value prop is empty
        setPhoneValue('');
        setSelectedCountry(countries[0]);
      }
    }, [value]);

    // Handle click outside to close dropdown
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsDropdownOpen(false);
        }
      };

      if (isDropdownOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isDropdownOpen]);

    const handleCountrySelect = (country: CountryCode) => {
      setSelectedCountry(country);
      setIsDropdownOpen(false);
      onCountryChange?.(country);

      // Update phone number with new country code (only if there's a phone number)
      if (phoneValue && phoneValue.trim() !== '') {
        const fullPhoneNumber = country.dialCode + phoneValue;

        // Create synthetic event with full phone number for onChange
        if (onChange) {
          const syntheticEvent = {
            target: { value: fullPhoneNumber },
            currentTarget: { value: fullPhoneNumber },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(syntheticEvent);
        }
      }
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      // Only allow numbers
      const numericValue = value.replace(/[^0-9]/g, '');
      setPhoneValue(numericValue);

      // Only send full phone number if there's actually a phone number
      const fullPhoneNumber = numericValue.trim() ? selectedCountry.dialCode + numericValue : '';
      onPhoneChange?.(fullPhoneNumber);

      // Create synthetic event with full phone number for onChange
      if (onChange) {
        const syntheticEvent = {
          ...e,
          target: { ...e.target, value: fullPhoneNumber },
          currentTarget: { ...e.currentTarget, value: fullPhoneNumber },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow: backspace, delete, tab, escape, enter, home, end, left, right, up, down
      if (
        [8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true)
      ) {
        return;
      }
      // Ensure that it is a number and stop the keypress
      if ((e.shiftKey || e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
        e.preventDefault();
      }
    };

    const inputClasses = [styles.input, error && styles.error, styles[`size-${size}`], className]
      .filter(Boolean)
      .join(' ');

    const containerClasses = [
      styles.inputContainer,
      error && styles.error,
      styles[`size-${size}`],
      fullWidth && styles.fullWidth,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={styles.inputField}>
        {label && (
          <Typography
            variant="text-sm"
            as="label"
            color={error ? 'error' : 'secondary'}
            weight="normal"
            className={styles.label}
          >
            {label}
            {required && <span className={styles.required}>*</span>}
          </Typography>
        )}

        <div className={containerClasses}>
          {/* Country Code Dropdown */}
          <div className={styles.countrySelector} ref={dropdownRef}>
            <button
              type="button"
              className={styles.countryButton}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <span className={styles.countryCode}>{selectedCountry.code}</span>
              <MdKeyboardArrowDown
                size={16}
                className={`${styles.dropdownIcon} ${isDropdownOpen ? styles.rotated : ''}`}
              />
            </button>

            {isDropdownOpen && (
              <div className={styles.dropdownMenu}>
                {countries.map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    className={`${styles.dropdownItem} ${
                      selectedCountry.code === country.code ? styles.active : ''
                    }`}
                    onClick={() => handleCountrySelect(country)}
                  >
                    <span className={styles.countryFlag}>{country.flag}</span>
                    <span className={styles.countryName}>{country.name}</span>
                    <span className={styles.countryDialCode}>{country.dialCode}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className={styles.divider} />

          {/* Phone Number Input */}
          <input
            ref={ref}
            className={inputClasses}
            type="tel"
            placeholder="(555) 000-0000"
            value={phoneValue}
            onChange={handlePhoneChange}
            onKeyDown={handleKeyDown}
            {...(() => {
              const { value: _, ...restProps } = props as any;
              return restProps;
            })()}
          />
        </div>

        {error && (
          <Typography
            variant="text-sm"
            as="div"
            color="error"
            weight="normal"
            className={styles.errorMessage}
          >
            {error}
          </Typography>
        )}

        {helperText && !error && (
          <Typography
            variant="text-sm"
            as="div"
            color="disabled"
            weight="normal"
            className={styles.helperText}
          >
            {helperText}
          </Typography>
        )}
      </div>
    );
  }
);

PhoneNumberInput.displayName = 'PhoneNumberInput';

export default PhoneNumberInput;
